import React from 'react';
import { Filter, FilterNumberValue } from './types';
import Popover from '@teetsh/app/src/components/Popover';
import PopoverSpan from '@teetsh/app/src/components/Popover/PopoverSpan';
import tw, { css } from 'twin.macro';
import { FilterOptions } from '@teetsh/app/src/containers/Eleves/List/Table/FilterBar/FilterOptions';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { isDefined } from 'types/utils';

interface Props {
  filter: Filter;
  onChange: (v: string[] | boolean | FilterNumberValue) => void;
  onDelete: (v: any) => void;
  buttonRef: any;
}

function getButtonTitleBasedOnFilterType(
  filter: Filter,
  t: TFunction<['common']>,
) {
  if (filter.fieldType === 'select' || filter.fieldType === 'multiselect') {
    return (filter.filterValue as string[])
      .map(
        v =>
          ((filter as any)?.options || []).find(opt => opt.value === v)?.label,
      )
      .filter(isDefined)
      .join(', ');
  }
  if (filter.fieldType === 'bool') {
    return filter.filterValue ? t('common:values.yes') : t('common:values.no');
  }
  if (filter.fieldType === 'number') {
    const [min, max] = filter.filterValue;
    if (!min && min !== 0 && !max && max !== 0) {
      return t('common:filters.numberRange', { min: '...', max: '...' });
    }
    if (!max && max !== 0) {
      return `≥ ${min}`;
    }
    if (!min && min !== 0) {
      return `≤ ${max}`;
    }
    if (min == max) {
      return `= ${min}`;
    }
    return t('common:filters.numberRange', { min, max });
  }
  return filter.filterValue;
}

export function FilterButton(props: Props) {
  const { filter, onChange, onDelete } = props;
  const { t } = useTranslation(['common']);
  return (
    <Popover
      onClose={() => {
        if (filter?.fieldType === 'number') {
          const [min, max]: FilterNumberValue = filter.filterValue;
          if ((!!min || min === 0) && (!!max || max === 0) && min > max) {
            onChange([max, min]);
          }
        }
      }}
      as="div"
    >
      <PopoverSpan tw="m-1 inline-flex rounded-full border border-neutral-quiet items-center py-1.5 pl-3 pr-2 text-sm font-medium bg-white text-neutral-intense cursor-pointer">
        <span
          ref={props.buttonRef}
          css={[
            filter.fieldType !== 'number' && [
              tw`truncate`,
              css`
                max-width: 8rem;
              `,
            ],
          ]}
        >
          <span tw="text-xs text-muted mr-1">{filter.fieldLabel}</span>{' '}
          {getButtonTitleBasedOnFilterType(filter, t)}
        </span>
        <button
          type="button"
          tw="shrink-0 ml-1 h-4 w-4 p-1 rounded-full inline-flex text-neutral-quiet hover:bg-neutral-quiet hover:text-neutral-quiet "
          aria-label={`Supprimer le filtre pour ${filter.fieldLabel}`}
          onClick={e => {
            e.stopPropagation();
            onDelete([]);
          }}
        >
          <svg tw="h-2 w-2" stroke="currentColor" fill="none" viewBox="0 0 8 8">
            <path
              strokeLinecap="round"
              strokeWidth="1.5"
              d="M1 1l6 6m0-6L1 7"
            />
          </svg>
        </button>
      </PopoverSpan>
      <Popover.Panel>
        <div data-testid="filter-options" tw="p-4">
          <FilterOptions filter={filter} onChange={onChange} />
        </div>
      </Popover.Panel>
    </Popover>
  );
}
